'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Car, BookOpen, Star, MapPin, ToggleLeft, ToggleRight, CheckCircle, XCircle, LogOut, Menu, X, Bell, Clock, Eye, Trash2, RefreshCw } from 'lucide-react';
import AdminBookingsChart from './AdminBookingsChart';

// BACKEND INTEGRATION: Replace with useEffect + fetch from /api/admin/stats
const mockStats = [
  { id: 'stat-fleet', label: 'Fleet Available', value: '3', sub: 'of 5 vehicles', icon: Car, color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/20' },
  { id: 'stat-bookings', label: 'Active Bookings', value: '7', sub: 'today', icon: BookOpen, color: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/20' },
  { id: 'stat-reviews', label: 'Pending Reviews', value: '3', sub: 'awaiting approval', icon: Star, color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20' },
  { id: 'stat-destinations', label: 'Destinations', value: '11', sub: 'active packages', icon: MapPin, color: 'text-violet-400', bg: 'bg-violet-400/10', border: 'border-violet-400/20' },
];

interface VehicleAdmin {
  id: string;
  name: string;
  type: string;
  pricePerKm: number;
  available: boolean;
  bookings: number;
}

const initialVehicles: VehicleAdmin[] = [
  { id: 'adm-v-dzire', name: 'Swift Dzire', type: 'Sedan', pricePerKm: 14, available: true, bookings: 23 },
  { id: 'adm-v-innova', name: 'Innova Crysta', type: 'SUV', pricePerKm: 18, available: true, bookings: 18 },
  { id: 'adm-v-ertiga', name: 'Ertiga', type: 'MPV', pricePerKm: 16, available: false, bookings: 15 },
  { id: 'adm-v-aura', name: 'Hyundai Aura', type: 'Sedan', pricePerKm: 13, available: true, bookings: 31 },
  { id: 'adm-v-tempo', name: 'Tempo Traveller', type: 'Van', pricePerKm: 22, available: true, bookings: 9 },
];

interface Booking {
  id: string;
  customer: string;
  phone: string;
  vehicle: string;
  pickup: string;
  drop: string;
  date: string;
  status: 'confirmed' | 'completed' | 'pending' | 'cancelled';
  type: 'delhi' | 'outstation';
  amount: string;
}

const mockBookings: Booking[] = [
  { id: 'bk-2026-0091', customer: 'Priya Sharma', phone: '+91 98102 34567', vehicle: 'Innova Crysta', pickup: 'Shalimar Bagh', drop: 'IGI Airport T3', date: '12 May 2026, 08:30', status: 'confirmed', type: 'delhi', amount: '₹1,240' },
  { id: 'bk-2026-0090', customer: 'Rahul Verma', phone: '+91 99110 87654', vehicle: 'Swift Dzire', pickup: 'Connaught Place', drop: 'Noida Sector 62', date: '12 May 2026, 07:15', status: 'completed', type: 'delhi', amount: '₹560' },
  { id: 'bk-2026-0089', customer: 'Sunita Agarwal', phone: '+91 97300 11223', vehicle: 'Tempo Traveller', pickup: 'Shalimar Bagh', drop: 'Kedarnath Base Camp', date: '10 May 2026, 05:00', status: 'completed', type: 'outstation', amount: '₹14,500' },
  { id: 'bk-2026-0088', customer: 'Amit Bhatia', phone: '+91 96200 55443', vehicle: 'Ertiga', pickup: 'Rohini Sector 14', drop: 'Jaipur City Center', date: '09 May 2026, 06:30', status: 'completed', type: 'outstation', amount: '₹6,500' },
  { id: 'bk-2026-0087', customer: 'Kavita Mehta', phone: '+91 95100 66778', vehicle: 'Hyundai Aura', pickup: 'Pitampura', drop: 'New Delhi Railway Station', date: '12 May 2026, 11:00', status: 'pending', type: 'delhi', amount: '₹380' },
  { id: 'bk-2026-0086', customer: 'Deepak Joshi', phone: '+91 94000 99887', vehicle: 'Swift Dzire', pickup: 'Dwarka Sector 10', drop: 'Agra Taj Mahal Gate', date: '11 May 2026, 07:00', status: 'completed', type: 'outstation', amount: '₹4,500' },
  { id: 'bk-2026-0085', customer: 'Neha Gupta', phone: '+91 93000 44556', vehicle: 'Innova Crysta', pickup: 'Greater Kailash', drop: 'Chandigarh Sector 17', date: '08 May 2026, 09:00', status: 'cancelled', type: 'outstation', amount: '₹5,200' },
  { id: 'bk-2026-0084', customer: 'Vikram Singh', phone: '+91 92100 33221', vehicle: 'Tempo Traveller', pickup: 'Karol Bagh', drop: 'Kashmir Srinagar', date: '05 May 2026, 04:00', status: 'completed', type: 'outstation', amount: '₹32,000' },
];

interface ReviewAdmin {
  id: string;
  name: string;
  rating: number;
  text: string;
  trip: string;
  date: string;
  approved: boolean;
}

const initialReviews: ReviewAdmin[] = [
  { id: 'rev-pending-001', name: 'Mohan Lal', rating: 5, text: 'Best cab service in Delhi! Driver was very professional and car was spotless. Will book again.', trip: 'Delhi → Mathura', date: '11 May 2026', approved: false },
  { id: 'rev-pending-002', name: 'Anita Rajput', rating: 4, text: 'Good service for airport drop. On time and comfortable. Slightly expensive but worth it.', trip: 'Delhi Local', date: '10 May 2026', approved: false },
  { id: 'rev-pending-003', name: 'Sanjay Tiwari', rating: 5, text: 'Char Dham yatra with Navya Travels was a life-changing experience. Satish ji ne bahut help ki.', trip: 'Char Dham Yatra', date: '09 May 2026', approved: false },
  { id: 'rev-approved-001', name: 'Priya Sharma', rating: 5, text: 'Excellent service! Highly recommended!', trip: 'Delhi → Kedarnath', date: '28 Apr 2026', approved: true },
  { id: 'rev-approved-002', name: 'Rahul Verma', rating: 5, text: 'OTP system is great for security.', trip: 'Delhi Local', date: '15 May 2026', approved: true },
];

type AdminTab = 'overview' | 'vehicles' | 'bookings' | 'reviews';

// BACKEND INTEGRATION: Replace with real auth check — validate session token from cookie/localStorage
const ADMIN_CREDENTIALS = { email: 'satish@navyatravels.in', password: 'Navya@2026' };

export default function AdminDashboardClient() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [vehicles, setVehicles] = useState<VehicleAdmin[]>(initialVehicles);
  const [reviews, setReviews] = useState<ReviewAdmin[]>(initialReviews);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3000);
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (loginEmail === ADMIN_CREDENTIALS.email && loginPassword === ADMIN_CREDENTIALS.password) {
      setIsAuthenticated(true);
      setLoginError('');
    } else {
      setLoginError('Invalid credentials. Use the demo credentials shown below.');
    }
  };

  // BACKEND INTEGRATION: Replace with PATCH /api/admin/vehicles/:id to toggle availability
  const toggleAvailability = (vehicleId: string) => {
    setVehicles((prev) =>
      prev.map((v) =>
        v.id === vehicleId ? { ...v, available: !v.available } : v
      )
    );
    const v = vehicles.find((v) => v.id === vehicleId);
    if (v) showToast(`${v.name} marked as ${v.available ? 'Booked' : 'Available'}`);
  };

  // BACKEND INTEGRATION: Replace with PATCH /api/admin/reviews/:id/approve
  const approveReview = (reviewId: string) => {
    setReviews((prev) =>
      prev.map((r) => (r.id === reviewId ? { ...r, approved: true } : r))
    );
    showToast('Review approved and published.');
  };

  // BACKEND INTEGRATION: Replace with DELETE /api/admin/reviews/:id
  const deleteReview = (reviewId: string) => {
    setReviews((prev) => prev.filter((r) => r.id !== reviewId));
    showToast('Review deleted.');
  };

  const navItems: { key: AdminTab; label: string; icon: React.ElementType; badge?: number }[] = [
    { key: 'overview', label: 'Overview', icon: Clock },
    { key: 'vehicles', label: 'Fleet Management', icon: Car },
    { key: 'bookings', label: 'Bookings', icon: BookOpen, badge: mockBookings.filter((b) => b.status === 'pending').length },
    { key: 'reviews', label: 'Reviews', icon: Star, badge: reviews.filter((r) => !r.approved).length },
  ];

  const statusColors: Record<string, string> = {
    confirmed: 'bg-primary/15 text-primary border-primary/25',
    completed: 'bg-green-500/15 text-green-400 border-green-500/25',
    pending: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
    cancelled: 'bg-red-500/15 text-red-400 border-red-500/25',
  };

  // ─── Login Gate ───────────────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md glass-card border border-border/30 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-8">
            <AppLogo size={40} />
            <div>
              <span className="text-lg font-bold text-white block">Admin Panel</span>
              <span className="text-xs text-primary">Navya Tour & Travels</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-1">Secure Login</h2>
          <p className="text-muted-foreground text-sm mb-6">Owner access only — Satish Saini</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">Email Address</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="Admin email"
                className="input-dark w-full px-4 py-3 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  placeholder="Admin password"
                  className="input-dark w-full px-4 py-3 pr-12 text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white transition-colors"
                >
                  {showPassword ? <XCircle size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {loginError && (
              <p className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg px-3 py-2">
                {loginError}
              </p>
            )}

            <button
              type="submit"
              className="btn-primary w-full py-3 rounded-xl font-bold text-base"
            >
              Sign In to Admin Panel
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 p-4 bg-primary/5 border border-primary/15 rounded-xl">
            <p className="text-xs font-bold text-primary mb-2 uppercase tracking-wider">Demo Credentials</p>
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Email:</span>
                <button
                  onClick={() => setLoginEmail('satish@navyatravels.in')}
                  className="text-xs text-white font-mono bg-muted/30 px-2 py-0.5 rounded hover:bg-primary/20 transition-colors"
                >
                  satish@navyatravels.in
                </button>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">Password:</span>
                <button
                  onClick={() => setLoginPassword('Navya@2026')}
                  className="text-xs text-white font-mono bg-muted/30 px-2 py-0.5 rounded hover:bg-primary/20 transition-colors"
                >
                  Navya@2026
                </button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Click values to autofill</p>
          </div>

          <div className="mt-4 text-center">
            <Link href="/" className="text-xs text-muted-foreground hover:text-primary transition-colors">
              ← Back to Website
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ─── Dashboard ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full z-40 w-64 bg-card border-r border-border/30 flex flex-col transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Sidebar Header */}
        <div className="p-5 border-b border-border/30 flex items-center gap-3">
          <AppLogo size={36} />
          <div>
            <span className="text-sm font-bold text-white block leading-tight">Admin Panel</span>
            <span className="text-xs text-primary">Navya Tour & Travels</span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-muted-foreground hover:text-white"
            aria-label="Close sidebar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-4 space-y-1">
          <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-3 px-2">
            Management
          </p>
          {navItems.map((item) => (
            <button
              key={`admin-nav-${item.key}`}
              onClick={() => { setActiveTab(item.key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === item.key
                  ? 'bg-primary/15 text-primary border border-primary/20' :'text-muted-foreground hover:text-white hover:bg-muted/30'
              }`}
            >
              <item.icon size={17} />
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge ? (
                <span className="text-xs bg-amber-400 text-black font-bold px-1.5 py-0.5 rounded-full">
                  {item.badge}
                </span>
              ) : null}
            </button>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
              S
            </div>
            <div>
              <p className="text-xs font-bold text-white">Satish Saini</p>
              <p className="text-xs text-muted-foreground">Owner</p>
            </div>
          </div>
          <button
            onClick={() => setIsAuthenticated(false)}
            className="w-full flex items-center gap-2 text-xs text-muted-foreground hover:text-red-400 transition-colors py-2 px-3 rounded-lg hover:bg-red-400/10"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Sidebar Overlay (mobile) */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="sticky top-0 z-20 glass-nav px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-muted-foreground hover:text-white"
            aria-label="Open sidebar"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-base font-bold text-white capitalize">
              {navItems.find((n) => n.key === activeTab)?.label ?? 'Overview'}
            </h1>
            <p className="text-xs text-muted-foreground">12 May 2026 — Last synced just now</p>
          </div>
          <button className="relative p-2 rounded-lg glass-card border border-border/20 text-muted-foreground hover:text-white transition-colors">
            <Bell size={16} />
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400" />
          </button>
          <Link
            href="/"
            className="text-xs text-muted-foreground hover:text-primary transition-colors hidden sm:block"
          >
            View Website →
          </Link>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 lg:p-8 xl:p-10 max-w-screen-2xl mx-auto w-full">

          {/* ── OVERVIEW TAB ── */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {mockStats.map((stat) => (
                  <div
                    key={stat.id}
                    className={`glass-card border ${stat.border} rounded-2xl p-5`}
                  >
                    <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center mb-3`}>
                      <stat.icon size={20} className={stat.color} />
                    </div>
                    <p className="text-2xl font-extrabold text-white tabular-nums">{stat.value}</p>
                    <p className="text-sm font-semibold text-white mt-0.5">{stat.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{stat.sub}</p>
                  </div>
                ))}
              </div>

              {/* Bookings Chart */}
              <div className="glass-card border border-border/20 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-white text-base">Bookings This Week</h3>
                    <p className="text-xs text-muted-foreground">Delhi local + outstation combined</p>
                  </div>
                  <span className="text-xs text-green-400 font-semibold bg-green-400/10 px-2 py-1 rounded-full">
                    +18% vs last week
                  </span>
                </div>
                <AdminBookingsChart />
              </div>

              {/* Recent Bookings Preview */}
              <div className="glass-card border border-border/20 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-white text-base">Recent Bookings</h3>
                  <button
                    onClick={() => setActiveTab('bookings')}
                    className="text-xs text-primary hover:underline"
                  >
                    View All →
                  </button>
                </div>
                <div className="space-y-3">
                  {mockBookings.slice(0, 4).map((b) => (
                    <div
                      key={b.id}
                      className="flex items-center gap-3 py-2.5 border-b border-border/20 last:border-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary shrink-0">
                        {b.customer.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{b.customer}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {b.pickup} → {b.drop}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-bold text-white">{b.amount}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${statusColors[b.status]}`}>
                          {b.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fleet Quick Status */}
              <div className="glass-card border border-border/20 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-white text-base">Fleet Quick Status</h3>
                  <button
                    onClick={() => setActiveTab('vehicles')}
                    className="text-xs text-primary hover:underline"
                  >
                    Manage Fleet →
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                  {vehicles.map((v) => (
                    <div
                      key={v.id}
                      className={`rounded-xl p-3 border ${
                        v.available
                          ? 'bg-green-400/5 border-green-400/20' :'bg-red-400/5 border-red-400/20'
                      }`}
                    >
                      <p className="text-sm font-bold text-white">{v.name}</p>
                      <p className="text-xs text-muted-foreground mb-2">{v.type}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        v.available ? 'badge-available' : 'badge-booked'
                      }`}>
                        {v.available ? '● Available' : '● Booked'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── VEHICLES TAB ── */}
          {activeTab === 'vehicles' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">Fleet Management</h2>
                  <p className="text-sm text-muted-foreground">Toggle availability in real-time</p>
                </div>
                <button className="btn-primary px-4 py-2 text-sm rounded-lg flex items-center gap-2">
                  <Car size={16} /> Add Vehicle
                </button>
              </div>

              <div className="glass-card border border-border/20 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/30">
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Vehicle</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Type</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Rate</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Total Bookings</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Toggle</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.map((v, idx) => (
                        <tr
                          key={v.id}
                          className={`border-b border-border/20 last:border-0 hover:bg-primary/5 transition-colors ${
                            idx % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.01]'
                          }`}
                        >
                          <td className="px-5 py-4">
                            <span className="font-semibold text-white">{v.name}</span>
                          </td>
                          <td className="px-5 py-4 text-muted-foreground">{v.type}</td>
                          <td className="px-5 py-4 text-amber-300 font-semibold text-xs">
                            Contact for fare
                          </td>
                          <td className="px-5 py-4 text-muted-foreground tabular-nums">{v.bookings}</td>
                          <td className="px-5 py-4">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${
                              v.available ? 'badge-available' : 'badge-booked'
                            }`}>
                              {v.available ? 'Available' : 'Booked'}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <button
                              onClick={() => toggleAvailability(v.id)}
                              className={`flex items-center gap-2 text-xs font-semibold transition-colors ${
                                v.available ? 'text-green-400 hover:text-red-400' : 'text-red-400 hover:text-green-400'
                              }`}
                              aria-label={`Toggle ${v.name} availability`}
                            >
                              {v.available ? (
                                <><ToggleRight size={22} /> Mark Booked</>
                              ) : (
                                <><ToggleLeft size={22} /> Mark Available</>
                              )}
                            </button>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                                aria-label={`Edit ${v.name}`}
                              >
                                <RefreshCw size={14} />
                              </button>
                              <button
                                className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors"
                                aria-label={`Delete ${v.name}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Info */}
              <div className="glass-card border border-primary/15 rounded-xl p-4">
                <p className="text-xs text-muted-foreground">
                  <span className="text-primary font-semibold">Note:</span> Toggling a vehicle to &quot;Booked&quot; will immediately disable the booking button on the website for that vehicle. Changes are reflected in real-time for customers.
                </p>
              </div>
            </div>
          )}

          {/* ── BOOKINGS TAB ── */}
          {activeTab === 'bookings' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h2 className="text-xl font-bold text-white">All Bookings</h2>
                  <p className="text-sm text-muted-foreground">{mockBookings.length} total bookings</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {(['all', 'pending', 'confirmed', 'completed', 'cancelled'] as const).map((s) => (
                    <button
                      key={`bk-filter-${s}`}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold glass-card border border-border/20 text-muted-foreground hover:text-white transition-colors capitalize"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="glass-card border border-border/20 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead>
                      <tr className="border-b border-border/30">
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Booking ID</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Customer</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Vehicle</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Route</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Date</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Type</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Amount</th>
                        <th className="text-left px-5 py-3.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockBookings.map((b, idx) => (
                        <tr
                          key={b.id}
                          className={`border-b border-border/20 last:border-0 hover:bg-primary/5 transition-colors ${
                            idx % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.01]'
                          }`}
                        >
                          <td className="px-5 py-3.5">
                            <span className="text-xs font-mono text-muted-foreground">{b.id}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="font-semibold text-white text-sm">{b.customer}</p>
                            <p className="text-xs text-muted-foreground">{b.phone}</p>
                          </td>
                          <td className="px-5 py-3.5 text-muted-foreground">{b.vehicle}</td>
                          <td className="px-5 py-3.5">
                            <p className="text-xs text-white">{b.pickup}</p>
                            <p className="text-xs text-muted-foreground">→ {b.drop}</p>
                          </td>
                          <td className="px-5 py-3.5 text-xs text-muted-foreground whitespace-nowrap">{b.date}</td>
                          <td className="px-5 py-3.5">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              b.type === 'delhi' ?'bg-primary/10 text-primary' :'bg-violet-400/10 text-violet-400'
                            }`}>
                              {b.type === 'delhi' ? 'Delhi' : 'Outstation'}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 font-bold text-white tabular-nums">{b.amount}</td>
                          <td className="px-5 py-3.5">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border capitalize ${statusColors[b.status]}`}>
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── REVIEWS TAB ── */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-white">Review Management</h2>
                <p className="text-sm text-muted-foreground">
                  {reviews.filter((r) => !r.approved).length} pending approval
                </p>
              </div>

              {/* Pending Reviews */}
              {reviews.filter((r) => !r.approved).length > 0 && (
                <div>
                  <h3 className="text-sm font-bold text-amber-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Clock size={14} /> Pending Approval
                  </h3>
                  <div className="space-y-3">
                    {reviews.filter((r) => !r.approved).map((r) => (
                      <div
                        key={r.id}
                        className="glass-card border border-amber-400/20 rounded-xl p-5"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-bold text-white text-sm">{r.name}</span>
                              <span className="text-xs text-muted-foreground">{r.trip}</span>
                              <span className="text-xs text-muted-foreground">•</span>
                              <span className="text-xs text-muted-foreground">{r.date}</span>
                            </div>
                            <div className="flex gap-0.5 mb-2">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <span key={`${r.id}-s-${s}`} className={`text-xs ${s <= r.rating ? 'text-amber-400' : 'text-muted'}`}>
                                  ★
                                </span>
                              ))}
                            </div>
                            <p className="text-sm text-slate-300">{r.text}</p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <button
                              onClick={() => approveReview(r.id)}
                              className="flex items-center gap-1 text-xs font-semibold text-green-400 bg-green-400/10 border border-green-400/20 px-3 py-1.5 rounded-lg hover:bg-green-400/20 transition-colors"
                            >
                              <CheckCircle size={13} /> Approve
                            </button>
                            <button
                              onClick={() => deleteReview(r.id)}
                              className="flex items-center gap-1 text-xs font-semibold text-red-400 bg-red-400/10 border border-red-400/20 px-3 py-1.5 rounded-lg hover:bg-red-400/20 transition-colors"
                            >
                              <XCircle size={13} /> Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approved Reviews */}
              <div>
                <h3 className="text-sm font-bold text-green-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CheckCircle size={14} /> Published Reviews
                </h3>
                <div className="space-y-3">
                  {reviews.filter((r) => r.approved).map((r) => (
                    <div
                      key={r.id}
                      className="glass-card border border-green-400/15 rounded-xl p-5 flex items-start gap-4"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-white text-sm">{r.name}</span>
                          <span className="text-xs text-green-400 font-semibold">✓ Published</span>
                          <span className="text-xs text-muted-foreground">{r.trip}</span>
                        </div>
                        <p className="text-sm text-slate-300">{r.text}</p>
                      </div>
                      <button
                        onClick={() => deleteReview(r.id)}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-400/10 transition-colors shrink-0"
                        aria-label="Delete review"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 glass-card border border-primary/20 text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-glow animate-slide-up flex items-center gap-2">
          <CheckCircle size={16} className="text-green-400" />
          {toastMessage}
        </div>
      )}
    </div>
  );
}